import { escapeJsonForHtml } from "./html.js";

/**
 * Names of the stub methods that queue calls until the real SDK loads.
 * Must mirror `PUBLIC_METHODS` consumed by `autoInit` in `@ets/web`.
 */
export const STUB_METHODS = [
  "page",
  "track",
  "identify",
  "group",
  "alias",
  "flush",
  "ready",
  "reset",
] as const;

/**
 * Build the inline JS body that creates the `window.ets` stub.
 *
 * Used by two ingestion paths:
 *
 * - Snippet flow (`@ets/web/snippet`): customer pastes a single `<script>`
 *   tag containing this stub plus a CDN script loader.
 * - Interceptor / Layer 0 flow: the interceptor injects this stub into the
 *   upstream HTML and a separate `loader.js` script tag. Both paths share
 *   the same stub shape so `autoInit` does not need to know which one ran.
 *
 * Stub fields:
 *   _q  — queue of `[method, args]` tuples replayed once lite loads.
 *   _k  — sdk key (the SDK reads this as `writeKey`).
 *   _h  — bundle hash (used for tamper detection in autoInit; pass "" to
 *         opt out).
 *   _t  — capture timing (`{t,o,r,u}` = perf.now, perf.timeOrigin,
 *         document.referrer, location.href).
 */
export function buildStubBody(sdkKey: string, hash: string): string {
  return [
    "var t = performance.now(), o = performance.timeOrigin;",
    "var r = document.referrer;",
    "var u = location.href;",
    "var e = window.ets = {};",
    "e._q = [];",
    `e._k = ${escapeJsonForHtml(JSON.stringify(sdkKey))};`,
    `e._h = ${escapeJsonForHtml(JSON.stringify(hash))};`,
    "e._t = {t:t, o:o, r:r, u:u};",
    buildStubMethods(),
  ].join("\n");
}

function buildStubMethods(): string {
  const methods = STUB_METHODS.map((m) => JSON.stringify(m)).join(",");
  return [
    `[${methods}].forEach(function(m) {`,
    "  e[m] = function() {",
    "    e._q.push([m, [].slice.call(arguments)]);",
    "  };",
    "});",
  ].join("\n");
}

/**
 * Collapse multi-line stub JS to a single line. Trims each line and drops
 * empty lines; safe because the body never contains string literals that
 * span multiple source lines.
 */
export function minifyStubBody(js: string): string {
  return js
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("");
}
